import JSZip from 'jszip';

const MAX_ZIP_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_RETRIES = 3;

interface UploadProgress {
  onProgress: (progress: number) => void;
  onRetry: (attempt: number, error: Error) => void;
}

interface UploadOptions {
  onProgress?: (progress: number) => void;
  onRetry?: (attempt: number, error: Error) => void;
}

export class UploadService {
  private static async createZip(
    orderId: string,
    _customerName: string,
    files: File[],
    orderDoc: string
  ): Promise<Blob> {
    const zip = new JSZip();
    
    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > MAX_ZIP_SIZE) {
      throw new Error('Tamanho total dos arquivos excede 50MB');
    }
    
    for (const file of files) {
      zip.file(file.name, await file.arrayBuffer());
    }
    
    zip.file(`${orderId}.md`, orderDoc);
    
    return await zip.generateAsync({ type: 'blob' });
  }

  private static async uploadWithRetry(
    url: string,
    formData: FormData,
    retries = MAX_RETRIES,
    options: UploadOptions = {}
  ): Promise<Response> {
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok && retries > 0) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        options.onProgress?.(0);
        options.onRetry?.(MAX_RETRIES - retries + 1, error);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.uploadWithRetry(url, formData, retries - 1, options);
      }

      return response;
    } catch (error) {
      if (retries > 0) {
        options.onProgress?.(0);
        options.onRetry?.(MAX_RETRIES - retries + 1, error as Error);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.uploadWithRetry(url, formData, retries - 1, options);
      }
      throw error;
    }
  }

  static async uploadOrder(
    orderId: string,
    customerName: string,
    files: File[],
    orderDoc: string,
    options: UploadOptions = {}
  ): Promise<string> {
    try {
      if (!files.length) {
        throw new Error('Por favor, adicione arquivos antes de enviar');
      }

      options.onProgress?.(10);
      const zipBlob = await this.createZip(orderId, customerName, files, orderDoc);
      
      if (!zipBlob) {
        throw new Error('Erro ao criar arquivo ZIP');
      }
      options.onProgress?.(30);

      const sanitizedName = customerName
        .replace(/[^a-zA-Z0-9]/g, '')
        .substring(0, 10);

      const fileName = `${orderId}-${sanitizedName}.zip`;

      const formData = new FormData();
      formData.append('zipFile', zipBlob, fileName);
      formData.append('orderId', orderId);

      options.onProgress?.(50);
      
      const response = await this.uploadWithRetry(
        '/api/upload.php',
        formData,
        MAX_RETRIES,
        { 
          onProgress: (p) => options.onProgress?.(50 + p * 0.5),
          onRetry: (attempt, error) => {
            options.onRetry?.(attempt, error);
            console.error(`Tentativa ${attempt}/3: ${error.message}`);
          }
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Erro no envio do arquivo. Tente novamente.');
      }

      options.onProgress?.(100);
      return fileName;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido no upload';
      console.error('Erro detalhado:', errorMessage);
      throw new Error(`Falha no envio: ${errorMessage}`);
    }
  }
} 